/**
 * 随机源。见 docs/ARCHITECTURE.md §4.4。
 *
 * shared 只定义接口与两个**非密码学**实现：
 *  - `createSeededRng`：xoshiro128**，跨平台逐位确定性，仅供测试与可复现的 fuzz。
 *  - `createInsecureRng`：包 Math.random，仅供蒙特卡洛胜率估算。
 *
 * 红线 #15：真实发牌必须用 CSPRNG，而 CSPRNG 的实现**不在 shared**——
 * 服务端注入 `node:crypto.randomInt`，客户端注入 `crypto.getRandomValues`。
 * shared 若 import node:crypto 会让客户端打包失败，若内置 Math.random 洗牌
 * 则等于把「可预测的发牌」摆在手边等人误用。
 *
 * 为什么种子 PRNG 不能用来发牌：V8 的 Math.random 是 xorshift128+，
 * 观察约 4–5 个连续输出即可还原内部状态并预测全部后续输出；xoshiro128** 同理。
 * 任何 ≤64 位种子的 PRNG 都可被暴力枚举。
 */

export interface Rng {
  /** 返回 [0, maxExclusive) 内的均匀整数。maxExclusive ∈ [1, 2^31)。 */
  nextInt(maxExclusive: number): number
}

/** nextInt 的上界（不含）。与 §4.4 一致，留出整个 int32 正区间。 */
export const MAX_EXCLUSIVE_LIMIT = 0x8000_0000 // 2^31

const UINT32_RANGE = 0x1_0000_0000 // 2^32

function assertMaxExclusive(maxExclusive: number): void {
  if (
    !Number.isInteger(maxExclusive) ||
    maxExclusive < 1 ||
    maxExclusive >= MAX_EXCLUSIVE_LIMIT
  ) {
    // 调用方传错上界属于编程错误（不是玩家输入），直接抛而不是走 EngineResult
    throw new RangeError(
      `Rng.nextInt: maxExclusive must be an integer in [1, ${MAX_EXCLUSIVE_LIMIT}), got ${String(maxExclusive)}`,
    )
  }
}

/**
 * 把一个均匀的 uint32 源包成 Rng，用**拒绝采样**消除取模偏置。
 *
 * 直接 `raw % m` 时，前 `2^32 mod m` 个余数会多出现一次；m 较大时这个偏差足以
 * 系统性地偏袒牌堆中某些位置。做法是砍掉 `[limit, 2^32)` 这段尾巴，
 * 使剩余区间长度恰为 m 的整数倍。期望重抽次数 < 2（m < 2^31 时 < 1.0000001 次）。
 *
 * 客户端/服务端的 CSPRNG 适配器可以直接复用本函数，只需提供 uint32 源。
 */
export function rngFromUint32Source(nextUint32: () => number): Rng {
  return {
    nextInt(maxExclusive: number): number {
      assertMaxExclusive(maxExclusive)
      if (maxExclusive === 1) return 0
      const limit = UINT32_RANGE - (UINT32_RANGE % maxExclusive)
      let x = nextUint32() >>> 0
      while (x >= limit) x = nextUint32() >>> 0
      return x % maxExclusive
    },
  }
}

/** FNV-1a 32：把任意种子字符串压成一个 uint32，跨平台结果一致。 */
function fnv1a32(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** splitmix32：用单个 uint32 种子扩散出互不相关的状态字，避免相邻种子给出相似序列。 */
function splitmix32(seed: number): () => number {
  let a = seed | 0
  return () => {
    a = (a + 0x9e3779b9) | 0
    let t = a ^ (a >>> 16)
    t = Math.imul(t, 0x21f0aaad)
    t ^= t >>> 15
    t = Math.imul(t, 0x735a2d97)
    return (t ^ (t >>> 15)) >>> 0
  }
}

const rotl = (x: number, k: number): number => (x << k) | (x >>> (32 - k))

/**
 * xoshiro128**，跨平台确定性，**仅测试用**。
 *
 * 数字种子先转成十进制字符串再哈希：这样负数、小数、超过 2^32 的整数都有确定行为，
 * 不会因为 `seed | 0` 静默截断而让两个不同种子撞成同一条序列。
 */
export function createSeededRng(seed: number | string): Rng {
  const base = fnv1a32(typeof seed === 'string' ? seed : String(seed))
  const mix = splitmix32(base)
  let s0 = mix() | 0
  let s1 = mix() | 0
  let s2 = mix() | 0
  let s3 = mix() | 0
  // 全零状态是 xoshiro 的不动点，会永远输出 0
  if ((s0 | s1 | s2 | s3) === 0) s0 = 0x9e3779b9 | 0

  const nextUint32 = (): number => {
    const result = Math.imul(rotl(Math.imul(s1, 5), 7), 9) >>> 0
    const t = s1 << 9
    s2 ^= s0
    s3 ^= s1
    s1 ^= s2
    s0 ^= s3
    s2 ^= t
    s3 = rotl(s3, 11)
    return result
  }

  return rngFromUint32Source(nextUint32)
}

/**
 * 包 Math.random。**仅供蒙特卡洛胜率估算**（红线 #15 唯一放行 Math.random 的场景）。
 * 绝不可用于 `shuffleDeck`：Math.random 的内部状态可从少量输出反推。
 */
export function createInsecureRng(): Rng {
  return {
    nextInt(maxExclusive: number): number {
      assertMaxExclusive(maxExclusive)
      // Math.random 有 53 位精度，对 m < 2^31 的取整偏差约 2^-22 量级，
      // 对蒙特卡洛的统计结论无影响；这里不做拒绝采样以免拖慢热路径。
      const v = Math.floor(Math.random() * maxExclusive)
      return v >= maxExclusive ? maxExclusive - 1 : v
    },
  }
}
